import React from "react";
import { useStaticQuery, graphql, Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { Flex } from "theme-ui";

const icons = ["facebook", "instagram", "linkedin"] as const;
const logos = ["heights", "southern", "convington", "quick"] as const;

type LogoType = (typeof logos)[number];
type IconType = (typeof icons)[number];

interface Props {
  logo: LogoType;
  icons: IconType[];
  urls: string[];
  hideIcons?: boolean;
}

const LogoSocialBox = ({ logo, icons, urls, hideIcons }: Props) => {
  const data = useStaticQuery(graphql`
    query LogoSocialBoxQuery {
      facebook: file(relativePath: { eq: "social/facebook.png" }) {
        childImageSharp {
          gatsbyImageData(width: 40, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      instagram: file(relativePath: { eq: "social/instagram.png" }) {
        childImageSharp {
          gatsbyImageData(width: 40, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      linkedin: file(relativePath: { eq: "social/linkedin.png" }) {
        childImageSharp {
          gatsbyImageData(width: 40, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      heights: file(relativePath: { eq: "HeightsLogo_WebsiteVersion.png" }) {
        childImageSharp {
          gatsbyImageData(width: 150, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      southern: file(relativePath: { eq: "Southern Finance.png" }) {
        childImageSharp {
          gatsbyImageData(width: 150, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      quick: file(relativePath: { eq: "Quick Credit.png" }) {
        childImageSharp {
          gatsbyImageData(width: 150, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
      convington: file(relativePath: { eq: "Covington Credit.png" }) {
        childImageSharp {
          gatsbyImageData(width: 150, placeholder: NONE, formats: [WEBP, AVIF])
        }
      }
    }
  `);
  const {
    heights,
    facebook,
    instagram,
    linkedin,
    southern,
    quick,
    convington,
  } = data;

  const getIcon = (icon: string) => {
    switch (icon) {
      case "facebook":
        return (
          <GatsbyImage
            image={facebook.childImageSharp.gatsbyImageData}
            alt="Facebook Logo"
          />
        );
      case "instagram":
        return (
          <GatsbyImage
            image={instagram.childImageSharp.gatsbyImageData}
            alt="Instagram Logo"
          />
        );
      case "linkedin":
        return (
          <GatsbyImage
            image={linkedin.childImageSharp.gatsbyImageData}
            alt="LinkedIn Logo"
          />
        );
      default:
        break;
    }
  };

  const getLogo = (logo: LogoType) => {
    switch (logo) {
      case "heights":
        return (
          <Link to="/">
            <GatsbyImage
              image={heights.childImageSharp.gatsbyImageData}
              alt="Southern Finance Logo"
              sx={{
                mx: 3,
              }}
            />
          </Link>
        );
      case "southern":
        return (
          <Link to="/southern-finance">
            <GatsbyImage
              image={southern.childImageSharp.gatsbyImageData}
              alt="Southern Finance Logo"
              sx={{
                mx: 3,
              }}
            />
          </Link>
        );
      case "convington":
        return (
          <Link to="/covington-credit">
            <GatsbyImage
              image={convington.childImageSharp.gatsbyImageData}
              alt="Covington Credit Logo"
              sx={{
                mx: 3,
              }}
            />
          </Link>
        );
      case "quick":
        return (
          <Link to="/quick-credit">
            <GatsbyImage
              image={quick.childImageSharp.gatsbyImageData}
              alt="Quick Credit Logo"
              sx={{
                mx: 3,
              }}
            />
          </Link>
        );
    }
  };
  return (
    <Flex
      sx={{
        justfityContent: "center",
        alignItems: "center",
        flexDirection: "column",
        mx: [3, 4],
        mb: 4,
      }}
    >
      <Flex
        sx={{
          height: 100,
          alignItems: "center",
        }}
      >
        {getLogo(logo)}
      </Flex>
      <Flex
        sx={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {!hideIcons &&
          icons.map((icon: string, i: number) => (
            <a
              key={i}
              href={urls[i]}
              rel="noopener noreferrer"
              target="_blank"
              sx={{
                mx: 2,
                width: [30],
              }}
            >
              {getIcon(icon)}
            </a>
          ))}
      </Flex>
    </Flex>
  );
};

export default LogoSocialBox;
